import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  X, FileText, FileSpreadsheet, Presentation, GitBranch,
  CheckCircle2, CircleSlash, AlertTriangle, Loader2, Clock,
} from 'lucide-react';
import { portalDocumentsApi } from '../../services/api';

// Map docgen format -> icon + colour. Falls back to FileText.
const FORMAT_META = {
  docx: { Icon: FileText, color: '#3B82F6', label: 'DOCX' },
  xlsx: { Icon: FileSpreadsheet, color: '#10B981', label: 'XLSX' },
  pptx: { Icon: Presentation, color: '#F26B1F', label: 'PPTX' },
  mermaid: { Icon: GitBranch, color: '#A855F7', label: 'Diagram' },
  infographic: { Icon: GitBranch, color: '#EC4899', label: 'Infographic' },
};

// Map status -> chip styling. Canonical colours that match the inline
// "Stopped" / "DocGen Cancelled" pills shown in chat bubbles.
const STATUS_META = {
  ready:     { Icon: CheckCircle2, label: 'Completed',  cls: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/25' },
  cancelled: { Icon: CircleSlash,  label: 'Cancelled',  cls: 'text-amber-300 bg-amber-500/10 border-amber-400/25' },
  failed:    { Icon: AlertTriangle,label: 'Failed',     cls: 'text-rose-300 bg-rose-500/10 border-rose-400/25' },
  pending:   { Icon: Loader2,      label: 'Pending',    cls: 'text-sky-300 bg-sky-500/10 border-sky-400/25' },
  drafting:  { Icon: Loader2,      label: 'Drafting',   cls: 'text-sky-300 bg-sky-500/10 border-sky-400/25' },
  building:  { Icon: Loader2,      label: 'Building',   cls: 'text-sky-300 bg-sky-500/10 border-sky-400/25' },
  generating:{ Icon: Loader2,      label: 'Generating', cls: 'text-sky-300 bg-sky-500/10 border-sky-400/25' },
};

const formatTs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export const DocGenActivityDrawer = ({ open, onClose, conversationId }) => {
  const { data: jobs = [], isLoading, refetch } = useQuery({
    queryKey: ['docgen-history', conversationId],
    queryFn: () => portalDocumentsApi.historyForConversation(conversationId).then(r => r.data),
    enabled: !!open && !!conversationId,
    refetchInterval: open ? 4000 : false,  // live tail while drawer is open
    staleTime: 0,
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            data-testid="docgen-activity-backdrop"
          />
          <motion.aside
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            className="fixed top-0 right-0 z-50 h-full w-[420px] max-w-[92vw] bg-nexus-bg-primary border-l border-nexus-border-subtle shadow-2xl flex flex-col"
            data-testid="docgen-activity-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-nexus-border-subtle">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-nexus-text-tertiary mb-0.5">Audit log</p>
                <h2 className="text-base font-semibold text-nexus-text-primary">Document Activity</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-white/5 transition-colors"
                data-testid="docgen-activity-close"
                aria-label="Close activity log"
              >
                <X size={16} />
              </button>
            </div>

            {/* Subhead */}
            <div className="px-5 py-3 border-b border-nexus-border-subtle/50">
              <p className="text-xs text-nexus-text-secondary leading-relaxed">
                Every document this conversation has produced or attempted —
                with status, format, engine timestamps, and cancellation
                metadata for compliance review.
              </p>
            </div>

            {/* Timeline */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {!conversationId ? (
                <EmptyState
                  title="No conversation selected"
                  detail="Open a chat to see its document activity."
                />
              ) : isLoading ? (
                <div className="flex items-center justify-center py-12 text-nexus-text-tertiary text-xs gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading activity…
                </div>
              ) : jobs.length === 0 ? (
                <EmptyState
                  title="No documents yet"
                  detail="Document jobs started in this conversation will appear here as a chronological audit log."
                />
              ) : (
                <ol className="relative space-y-3" data-testid="docgen-activity-list">
                  <span className="absolute left-[15px] top-2 bottom-2 w-px bg-nexus-border-subtle" aria-hidden />
                  {jobs.map((j) => (
                    <TimelineRow key={j.job_id} job={j} />
                  ))}
                </ol>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-nexus-border-subtle flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.16em] text-nexus-text-tertiary">
                {jobs.length} {jobs.length === 1 ? 'entry' : 'entries'}
              </span>
              <button
                onClick={() => refetch()}
                className="text-[11px] text-nexus-accent-mint hover:text-nexus-accent-mint/80 transition-colors"
                data-testid="docgen-activity-refresh"
              >
                Refresh
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const TimelineRow = ({ job }) => {
  const fmt = FORMAT_META[job.format] || { Icon: FileText, color: '#94A3B8', label: (job.format || '').toUpperCase() };
  const st = STATUS_META[job.status] || STATUS_META.pending;
  const isLive = ['pending', 'drafting', 'building', 'generating'].includes(job.status);
  const tsPrimary = job.cancelled_at || job.updated_at || job.created_at;

  return (
    <li className="relative pl-9" data-testid={`docgen-activity-row-${job.job_id}`}>
      {/* Dot */}
      <span
        className="absolute left-2 top-2 w-[14px] h-[14px] rounded-full border-2 border-nexus-bg-primary flex items-center justify-center"
        style={{ backgroundColor: fmt.color }}
        aria-hidden
      >
        <fmt.Icon size={7} color="#fff" strokeWidth={3} />
      </span>

      <div className="bg-nexus-bg-secondary/50 border border-nexus-border-subtle rounded-xl px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider border ${st.cls}`}>
            <st.Icon size={9} strokeWidth={2.5} className={isLive ? 'animate-spin' : ''} />
            {st.label}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-mono">
            {fmt.label}
          </span>
          <span className="ml-auto text-[10px] text-nexus-text-tertiary flex items-center gap-1">
            <Clock size={9} />
            {formatTs(tsPrimary)}
          </span>
        </div>

        <p className="text-xs text-nexus-text-primary leading-relaxed line-clamp-2">
          {job.title || job.prompt_preview || 'Untitled document'}
        </p>

        {job.status === 'failed' && job.error && (
          <p className="mt-1.5 text-[10px] text-rose-300/80 line-clamp-2 italic">
            {job.error}
          </p>
        )}
      </div>
    </li>
  );
};

const EmptyState = ({ title, detail }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 text-nexus-text-tertiary">
    <FileText size={28} className="opacity-30 mb-3" />
    <p className="text-sm font-medium text-nexus-text-secondary mb-1">{title}</p>
    <p className="text-xs max-w-[260px]">{detail}</p>
  </div>
);

export default DocGenActivityDrawer;
